import React, { useState } from 'react';
import { postBacktestTarget, postRuleBacktest } from '../services/api';
import {
  TP_PCT_ALLOWED, SIZE_SOL_ALLOWED, ENTRY_OFFSET_ALLOWED, EXIT_LATENCY_ALLOWED,
  STOP_PCT_ALLOWED, MAX_PICKS_ALLOWED, PICK_ORDER_ALLOWED, MATRIX_MAX, DEFAULTS,
} from './backtestPresets';

/* Formulaire de flag backtest (réutilisable drill-down + page Backtests).
   Multi-select TP + multi-select size (matrice size×TP). Single-select offset/latence/stop/max-picks/pick-order.
   Validation client miroir des bornes serveur (≥1 TP, ≥1 size, matrice ≤25) ; serveur reste autoritaire.
   Réutilise les classes CSS .tdp-* (héritées du panel / page hôte). */

const toggle = (arr, v) => (arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v].sort((a, b) => a - b));

export default function BacktestLaunchForm({ kind, address, onLaunched, mode = 'entity', ruleId = null, slotTag = null, initialParams = null }) {
  const [tpList, setTpList] = useState(initialParams?.tp_list ?? DEFAULTS.tp_list);
  const [sizeList, setSizeList] = useState(initialParams?.size_list ?? DEFAULTS.size_list);
  const [offset, setOffset] = useState(DEFAULTS.entry_slot_offset);
  const [latency, setLatency] = useState(initialParams?.exit_latency_ms ?? DEFAULTS.exit_latency_ms);
  const [stop, setStop] = useState(initialParams?.stop_pct ?? DEFAULTS.stop_pct);
  const [maxPicks, setMaxPicks] = useState(initialParams?.max_picks ?? DEFAULTS.max_picks);
  const [pickOrder, setPickOrder] = useState(initialParams?.pick_order ?? DEFAULTS.pick_order);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const matrix = tpList.length * sizeList.length;
  const valid = tpList.length >= 1 && sizeList.length >= 1 && matrix <= MATRIX_MAX;

  const launch = async () => {
    if (!valid || busy) return;
    setBusy(true); setErr(null);
    try {
      if (mode === 'cohort') {
        // L'entrée est dérivée de la règle côté serveur (slot_tag) — pas d'entry_slot_offset.
        const params = {
          tp_list: [...tpList].sort((a, b) => a - b),
          size_list: [...sizeList].sort((a, b) => a - b),
          exit_latency_ms: latency,
          stop_pct: stop,
          max_picks: maxPicks,
          pick_order: pickOrder,
        };
        const res = await postRuleBacktest(ruleId, params);
        onLaunched && onLaunched(res.job_id);
      } else {
        const params = {
          tp_list: [...tpList].sort((a, b) => a - b),
          size_list: [...sizeList].sort((a, b) => a - b),
          entry_slot_offset: offset,
          exit_latency_ms: latency,
          stop_pct: stop,
          max_picks: maxPicks,
          pick_order: pickOrder,
        };
        const res = await postBacktestTarget({ kind, address, params });
        onLaunched && onLaunched(res.job_id);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'erreur');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="tdp-ctrl">
        <span className="lbl">TP %</span>
        {TP_PCT_ALLOWED.map(v => (
          <button key={v} className={`tdp-btn ${tpList.includes(v) ? 'on' : ''}`} onClick={() => setTpList(l => toggle(l, v))}>+{v}%</button>
        ))}
      </div>
      <div className="tdp-ctrl">
        <span className="lbl">Size SOL</span>
        {SIZE_SOL_ALLOWED.map(v => (
          <button key={v} className={`tdp-btn ${sizeList.includes(v) ? 'on' : ''}`} onClick={() => setSizeList(l => toggle(l, v))}>{v}</button>
        ))}
      </div>
      {mode === 'cohort' ? (
        <div className="tdp-ctrl">
          <span className="lbl">Entrée</span>
          <span className="tdp-btn on" style={{ cursor: 'default' }}>
            {slotTag === 'slot0' ? 'slot 0' : 'slot 1'} (dérivée de la règle)
          </span>
        </div>
      ) : (
        <div className="tdp-ctrl">
          <span className="lbl">Entry offset</span>
          {ENTRY_OFFSET_ALLOWED.map(v => (
            <button key={v} className={`tdp-btn ${offset === v ? 'on' : ''}`} onClick={() => setOffset(v)}>slot+{v}</button>
          ))}
        </div>
      )}
      <div className="tdp-ctrl">
        <span className="lbl">Latence sortie</span>
        {EXIT_LATENCY_ALLOWED.map(v => (
          <button key={v} className={`tdp-btn ${latency === v ? 'on' : ''}`} onClick={() => setLatency(v)}>{v}ms</button>
        ))}
      </div>
      <div className="tdp-ctrl">
        <span className="lbl">Stop %</span>
        {STOP_PCT_ALLOWED.map(v => (
          <button key={v} className={`tdp-btn ${stop === v ? 'on' : ''}`} onClick={() => setStop(v)}>{v === 0 ? 'off' : `-${v}%`}</button>
        ))}
      </div>
      <div className="tdp-ctrl">
        <span className="lbl">Max picks</span>
        {MAX_PICKS_ALLOWED.map(v => (
          <button key={v} className={`tdp-btn ${maxPicks === v ? 'on' : ''}`} onClick={() => setMaxPicks(v)}>{v}</button>
        ))}
      </div>
      <div className="tdp-ctrl">
        <span className="lbl">Ordre picks</span>
        {PICK_ORDER_ALLOWED.map(v => (
          <button key={v} className={`tdp-btn ${pickOrder === v ? 'on' : ''}`} onClick={() => setPickOrder(v)}>{v}</button>
        ))}
      </div>
      <div className="tdp-ctrl">
        <span className="lbl">Matrice {matrix}/{MATRIX_MAX}</span>
        <button className="tdp-btn on" disabled={!valid || busy} onClick={launch} style={{ opacity: (!valid || busy) ? 0.4 : 1 }}>
          {busy ? 'envoi…' : 'Lancer le backtest'}
        </button>
      </div>
      {!valid && <div className="tdp-sub" style={{ color: 'var(--amber,#ffb020)' }}>≥1 TP, ≥1 size, matrice ≤ {MATRIX_MAX}.</div>}
      {err && <div className="tdp-sub" style={{ color: 'var(--red,#ff3b5c)' }}>Erreur : {err}</div>}
    </div>
  );
}
